import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TextField, Button, Alert,
  TablePagination, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Grid, Card, CardContent, IconButton,
  Menu, MenuItem, ListItemIcon, ListItemText, Tabs, Tab,
  CircularProgress, LinearProgress, Accordion, AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import API_CONFIG, { getApiUrl } from '../../config/api';

const EmailManagement = () => {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    sent: 0,
    failed: 0,
    cancelled: 0,
    total: 0
  });
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuEmail, setMenuEmail] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchEmails = async (status = 'all') => {
    setLoading(true);
    setError('');
    try {
      const requestBody = {
        admin_password: 'Admin@123!!!'
      };

      if (dateFrom) requestBody.date_from = dateFrom;
      if (dateTo) requestBody.date_to = dateTo;
      if (status !== 'all') requestBody.status = status;

      const res = await fetch(getApiUrl('/admin/emails'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await res.json();
      if (data.error) throw new Error(data.error);

      setEmails(data.emails || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailStats = async () => {
    try {
      const res = await fetch(getApiUrl('/admin/email-stats'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_password: 'Admin@123!!!' })
      });

      const data = await res.json();
      if (data.stats) {
        setStats(data.stats);
      }
    } catch (e) {
      console.error('Error fetching email stats:', e);
    }
  };

  const processEmailQueue = async () => {
    setProcessing(true);
    try {
      const res = await fetch(getApiUrl('/admin/process-email-queue'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_password: 'Admin@123!!!' })
      });

      const data = await res.json();
      if (data.error) throw new Error(data.error);

      alert(`Email queue processed. Result: ${JSON.stringify(data.result)}`);
      fetchEmails(statusFilter);
      fetchEmailStats();
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(false);
    }
  };

  const cancelEmail = async (emailId) => {
    try {
      const res = await fetch(getApiUrl('/admin/cancel-email'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_password: 'Admin@123!!!',
          email_id: emailId
        })
      });

      const data = await res.json();
      if (data.error) throw new Error(data.error);

      alert('Email cancelled successfully');
      fetchEmails(statusFilter);
      fetchEmailStats();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMenuClick = (event, email) => {
    setAnchorEl(event.currentTarget);
    setMenuEmail(email);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuEmail(null);
  };

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
    setEmailDialogOpen(true);
    handleMenuClose();
  };

  const handleCancelEmail = (email) => {
    if (window.confirm('Are you sure you want to cancel this email?')) {
      cancelEmail(email.id);
    }
    handleMenuClose();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const statusMap = {
      0: 'all',
      1: 'pending',
      2: 'sent',
      3: 'failed',
      4: 'cancelled'
    };
    const status = statusMap[newValue];
    setStatusFilter(status);
    fetchEmails(status);
  };

  useEffect(() => {
    fetchEmails('all');
    fetchEmailStats();
  }, []);

  // Global search filter
  const filteredEmails = emails.filter(email => {
    if (!search) return true;
    const searchStr = search.toLowerCase();
    return (
      email.email_type.toLowerCase().includes(searchStr) ||
      email.recipient_email.toLowerCase().includes(searchStr) ||
      email.user_id.toLowerCase().includes(searchStr)
    );
  });

  // Sort emails
  const sortedEmails = [...filteredEmails].sort((a, b) => {
    let aValue, bValue;

    switch (orderBy) {
      case 'email_type':
        aValue = a.email_type || '';
        bValue = b.email_type || '';
        break;
      case 'recipient_email':
        aValue = a.recipient_email || '';
        bValue = b.recipient_email || '';
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'created_at':
      default:
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
        break;
    }

    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'sent': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      case 'processing': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <ScheduleIcon />;
      case 'sent': return <CheckCircleIcon />;
      case 'failed': return <ErrorIcon />;
      case 'cancelled': return <CancelIcon />;
      case 'processing': return <AccessTimeIcon />;
      default: return <EmailIcon />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'sent': return 'Sent';
      case 'failed': return 'Failed';
      case 'cancelled': return 'Cancelled';
      case 'processing': return 'Processing';
      default: return status;
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Email Management
      </Typography>

      {/* Email Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon color="warning" sx={{ mr: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Pending
                </Typography>
              </Box>
              <Typography variant="h3" component="div">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Sent
                </Typography>
              </Box>
              <Typography variant="h3" component="div">
                {stats.sent}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ErrorIcon color="error" sx={{ mr: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Failed
                </Typography>
              </Box>
              <Typography variant="h3" component="div">
                {stats.failed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CancelIcon color="action" sx={{ mr: 1 }} />
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Cancelled
                </Typography>
              </Box>
              <Typography variant="h3" component="div">
                {stats.cancelled}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="email tabs">
          <Tab label={`All Emails (${stats.total})`} />
          <Tab label={`Pending (${stats.pending})`} />
          <Tab label={`Sent (${stats.sent})`} />
          <Tab label={`Failed (${stats.failed})`} />
          <Tab label={`Cancelled (${stats.cancelled})`} />
        </Tabs>
      </Paper>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              placeholder="Search emails..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => fetchEmails(statusFilter)}
              fullWidth
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={processing ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={processEmailQueue}
              fullWidth
              disabled={processing || stats.pending === 0}
            >
              {processing ? 'Processing...' : 'Process Queue'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Emails Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email_type'}
                  direction={orderBy === 'email_type' ? order : 'asc'}
                  onClick={() => handleSort('email_type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'recipient_email'}
                  direction={orderBy === 'recipient_email' ? order : 'asc'}
                  onClick={() => handleSort('recipient_email')}
                >
                  Recipient
                </TableSortLabel>
              </TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleSort('created_at')}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEmails.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(email => (
              <TableRow key={email.id} hover>
                <TableCell>
                  <Chip
                    label={email.email_type}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{email.recipient_email}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {email.user_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon(email.status)}
                    <Chip
                      label={getStatusText(email.status)}
                      size="small"
                      color={getStatusColor(email.status)}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  {new Date(email.created_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  <IconButton onClick={(e) => handleMenuClick(e, email)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={sortedEmails.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Email Details Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Email Details - {selectedEmail?.email_type}
        </DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Email Information</Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Typography><strong>Type:</strong> {selectedEmail.email_type}</Typography>
                      <Typography><strong>Recipient:</strong> {selectedEmail.recipient_email}</Typography>
                      <Typography><strong>User ID:</strong> {selectedEmail.user_id}</Typography>
                      <Typography><strong>Status:</strong> {getStatusText(selectedEmail.status)}</Typography>
                      <Typography><strong>Created:</strong> {new Date(selectedEmail.created_at).toLocaleString()}</Typography>
                      {selectedEmail.sent_at && (
                        <Typography><strong>Sent:</strong> {new Date(selectedEmail.sent_at).toLocaleString()}</Typography>
                      )}
                      {selectedEmail.scheduled_at && (
                        <Typography><strong>Scheduled:</strong> {new Date(selectedEmail.scheduled_at).toLocaleString()}</Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Additional Details</Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Typography><strong>Retry Count:</strong> {selectedEmail.retry_count || 0}</Typography>
                      {selectedEmail.error_message && (
                        <Box>
                          <Typography variant="body2" color="error" gutterBottom>
                            Error Message:
                          </Typography>
                          <Typography variant="body2" sx={{ bgcolor: 'error.light', p: 1, borderRadius: 1 }}>
                            {selectedEmail.error_message}
                          </Typography>
                        </Box>
                      )}
                      {selectedEmail.template_data && (
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2">Template Data</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
                              {JSON.stringify(JSON.parse(selectedEmail.template_data), null, 2)}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Email Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewEmail(menuEmail)}>
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {menuEmail?.status === 'pending' && (
          <MenuItem onClick={() => handleCancelEmail(menuEmail)} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <CancelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cancel Email</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default EmailManagement;